import { createBrowserRouter } from 'react-router-dom'
import { ProtectedRoute } from './components/protected-route'
import GeneralError from './pages/errors/general-error'
import NotFoundError from './pages/errors/not-found-error'
import MaintenanceError from './pages/errors/maintenance-error'

const router = createBrowserRouter([
  // Auth routes
  {
    path: '/auth',
    children: [
      {
        path: 'sign-in',
        lazy: async () => ({
          Component: (await import('./pages/auth/sign-in')).default,
        }),
      },
    ],
  },

  // Main routes (protected)
  {
    path: '/',
    lazy: async () => {
      const AppShell = await import('./components/app-shell')
      return {
        Component: () => (
          <ProtectedRoute>
            <AppShell.default />
          </ProtectedRoute>
        ),
      }
    },
    errorElement: <GeneralError />,
    children: [
      {
        index: true,
        lazy: async () => {
          const { redirect } = await import('react-router-dom')
          return {
            loader: ({ request }) => {
              const url = new URL(request.url)
              const searchParams = new URLSearchParams(url.search)
              const redirectPath = `/projects${searchParams.toString() ? '?' + searchParams.toString() : ''}`
              return redirect(redirectPath)
            },
          }
        },
      },
      {
        path: 'projects',
        lazy: async () => ({
          Component: (await import('./pages/projects')).default,
        }),
      },
      {
        path: 'projects/new',
        lazy: async () => ({
          Component: (await import('./pages/projects/new')).default,
        }),
      },
      {
        path: 'projects/:projectId',
        lazy: async () => ({
          Component: (await import('./pages/projects/[id]')).default,
        }),
      },
      {
        path: 'tasks',
        lazy: async () => ({
          Component: (await import('./pages/tasks/all-tasks')).default,
        }),
      },
      {
        path: 'tasks/:id',
        lazy: async () => ({
          Component: (await import('./pages/tasks/detail')).default,
        }),
      },
      {
        path: 'projects/:projectId/tasks',
        lazy: async () => ({
          Component: (await import('./pages/tasks/all-tasks')).default,
        }),
      },
      {
        path: 'wiki',
        children: [
          {
            index: true,
            lazy: async () => ({
              Component: (await import('./pages/wiki')).default,
            }),
          },
          {
            path: ':pageId',
            children: [
              {
                index: true,
                lazy: async () => ({
                  Component: (await import('./pages/wiki')).default,
                }),
              },
              {
                path: 'version/:versionNumber',
                lazy: async () => ({
                  Component: (await import('./pages/wiki')).default,
                }),
              },
            ],
          },
        ],
      },
      {
        path: 'chats',
        lazy: async () => ({
          Component: (await import('./components/coming-soon')).default,
        }),
      },
      {
        path: 'apps',
        lazy: async () => ({
          Component: (await import('./pages/apps')).default,
        }),
      },
      {
        path: 'users',
        lazy: async () => ({
          Component: (await import('./pages/users')).default,
        }),
      },
      {
        path: 'roles',
        lazy: async () => ({
          Component: (await import('./pages/roles')).default,
        }),
      },
      {
        path: 'pkrIds',
        lazy: async () => ({
          Component: (await import('./pages/pkrIds')).default,
        }),
      },
      {
        path: 'analysis',
        lazy: async () => ({
          Component: (await import('./components/coming-soon')).default,
        }),
      },
      {
        path: 'extra-components',
        lazy: async () => ({
          Component: (await import('./pages/extra-components')).default,
        }),
      },
      {
        path: 'settings',
        lazy: async () => ({
          Component: (await import('./pages/settings')).default,
        }),
        errorElement: <GeneralError />,
        children: [
          {
            index: true,
            lazy: async () => ({
              Component: (await import('./pages/settings/profile')).default,
            }),
          },
          {
            path: 'account',
            lazy: async () => ({
              Component: (await import('./pages/settings/account')).default,
            }),
          },
          {
            path: 'appearance',
            lazy: async () => ({
              Component: (await import('./pages/settings/appearance')).default,
            }),
          },
          {
            path: 'notifications',
            lazy: async () => ({
              Component: (await import('./pages/settings/notifications'))
                .default,
            }),
          },
          {
            path: 'display',
            lazy: async () => ({
              Component: (await import('./pages/settings/display')).default,
            }),
          },
          {
            path: 'error-example',
            lazy: async () => ({
              Component: (await import('./pages/settings/error-example'))
                .default,
            }),
            errorElement: <GeneralError className='h-[50svh]' minimal />,
          },
        ],
      },
    ],
  },

  // Error routes
  { path: '/500', Component: GeneralError },
  { path: '/404', Component: NotFoundError },
  { path: '/503', Component: MaintenanceError },

  // Fallback 404 route
  { path: '*', Component: NotFoundError },
])

export default router
